import { RefObject, useCallback, useEffect, useState } from 'react';
import { Align, Side } from './dropdown.types';

type Position = { top: number; left: number };

const GAP = 4;

export const useDropdownPosition = (
  triggerRef: RefObject<HTMLElement>,
  contentRef: RefObject<HTMLDivElement>,
  side: Side,
  align: Align,
  open: boolean,
  portal: boolean
): Position => {
  const [pos, setPos] = useState<Position>({ top: 0, left: 0 });

  const calculate = useCallback(() => {
    const trigger = triggerRef.current;
    const content = contentRef.current;

    if (!trigger || !content) {
      return;
    }

    const tr = trigger.getBoundingClientRect();
    const cr = content.getBoundingClientRect();
    const scrollX = portal ? window.scrollX : 0;
    const scrollY = portal ? window.scrollY : 0;

    let top = 0;
    let left = 0;

    switch (side) {
      case 'bottom':
        top = tr.bottom + GAP + scrollY;
        break;
      case 'top':
        top = tr.top - cr.height - GAP + scrollY;
        break;
      case 'right':
        left = tr.right + GAP + scrollX;
        top = tr.top + scrollY;
        break;
      case 'left':
        left = tr.left - cr.width - GAP + scrollX;
        top = tr.top + scrollY;
        break;
      default:
        break;
    }

    if (side === 'bottom' || side === 'top') {
      switch (align) {
        case 'start':
          left = tr.left + scrollX;
          break;
        case 'center':
          left = tr.left + tr.width / 2 - cr.width / 2 + scrollX;
          break;
        case 'end':
          left = tr.right - cr.width + scrollX;
          break;
        default:
          break;
      }
    } else {
      switch (align) {
        case 'start':
          top = tr.top + scrollY;
          break;
        case 'center':
          top = tr.top + tr.height / 2 - cr.height / 2 + scrollY;
          break;
        case 'end':
          top = tr.bottom - cr.height + scrollY;
          break;
        default:
          break;
      }
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (portal) {
      if (left + cr.width > vw + scrollX) {
        left = vw + scrollX - cr.width - GAP;
      }

      if (left < scrollX) {
        left = scrollX + GAP;
      }

      if (top + cr.height > vh + scrollY) {
        top = tr.top - cr.height - GAP + scrollY;
      }

      if (top < scrollY) {
        top = scrollY + GAP;
      }
    }

    setPos({ top, left });
  }, [triggerRef, contentRef, side, align, portal]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const frame = requestAnimationFrame(calculate);
    window.addEventListener('scroll', calculate, true);
    window.addEventListener('resize', calculate);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', calculate, true);
      window.removeEventListener('resize', calculate);
    };
  }, [open, calculate]);

  return pos;
};
